import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Paper, Tooltip } from '@mui/material';
import { UserButton } from '@clerk/clerk-react';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SchoolIcon from '@mui/icons-material/School';
import SettingsIcon from '@mui/icons-material/Settings';

const MainLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { path: '/dashboard', icon: <DashboardIcon />, label: 'Dashboard' },
    { path: '/content', icon: <MenuBookIcon />, label: 'Content' },
    { path: '/practice', icon: <SchoolIcon />, label: 'Practice' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        backgroundImage: 'url(/background_1080_grey.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/background_1080_grey.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.2,
          zIndex: 0
        }
      }}
    >
      {/* Navigation Rail */}
      <Paper
        elevation={0}
        sx={{
          width: 80,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 2,
          borderRight: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'relative',
          zIndex: 1
        }}
      >
        {/* User Avatar - Using Clerk's UserButton with settings indicator */}
        <Tooltip title="Account & Settings" placement="right" arrow>
          <Box sx={{ mb: 2, position: 'relative' }}>
            <UserButton
              appearance={{
                elements: {
                  avatarBox: {
                    width: 56,
                    height: 56,
                  },
                  userButtonPopoverCard: {
                    pointerEvents: 'auto',
                  },
                }
              }}
            />
            {/* Settings indicator icon */}
            <Box
              sx={{
                position: 'absolute',
                bottom: -2,
                right: -2,
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid',
                borderColor: 'background.paper',
              }}
            >
              <SettingsIcon sx={{ fontSize: 12, color: 'primary.contrastText' }} />
            </Box>
          </Box>
        </Tooltip>

        {/* Logo/App Icon */}
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <img
            src="/logo/android-chrome-512x512.png"
            alt="Vocabulares"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </Box>

        {/* Navigation Items */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {navItems.map((item) => (
            <Tooltip key={item.path} title={item.label} placement="right" arrow>
              <Box
                onClick={() => navigate(item.path)}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  color: isActive(item.path) ? 'primary.contrastText' : 'text.secondary',
                  bgcolor: isActive(item.path) ? 'primary.main' : 'transparent',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: isActive(item.path)
                      ? 'primary.main'
                      : 'action.hover',
                  },
                  '&::before': isActive(item.path)
                    ? {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 4,
                        height: 32,
                        bgcolor: 'primary.main',
                        borderTopRightRadius: 2,
                        borderBottomRightRadius: 2,
                      }
                    : {},
                }}
              >
                {item.icon}
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Paper>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 3,
          position: 'relative',
          zIndex: 1
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default MainLayout;
